
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { GetLeakStatus, GetRepairType, LeakStatus, RepairType } from '@/api/types'
import { IsMobile } from '@/utils/check'
interface StatusOption {
  value: number
  label: string
}

interface RepairTypeOption {
  value: number
  label: string
}

@Component({
  name: 'SearchFilter'
})
export default class extends Vue {
  @Prop({ default: () => ({}) }) private initialFilter!: object
  @Prop({ default: () => ([LeakStatus.Unreported, LeakStatus.NeedAssignRepairDept]) }) private defaultStatus!: LeakStatus[]

  private filterParams = {
    status: this.defaultStatus,
    keywords: [''] as string[],
    addressKeywords: [''] as string[],
    createTimeStart: '',
    createTimeEnd: '',
    reportTimeStart: '',
    reportTimeEnd: '',
    repairTypes: [] as RepairType[],
    page: 1,
    pageSize: 10
  }

  // 移动端搜索弹窗
  private showSearchDialog = false

  // 日期范围选择器的值
  private dateRange: [string, string] | null = null

  // 年度筛选选择器的值
  private selectedYear: number | null = new Date().getFullYear()

  // 移动模式标记

  // 监听初始过滤条件变化
  @Watch('initialFilter', { immediate: true, deep: true })
  private onInitialFilterChange(val: any) {
    if (val && Object.keys(val).length > 0) {
      // 合并初始过滤条件
      this.filterParams = { ...this.filterParams, ...val }

      // 同步日期和年度
      if (val.reportTimeStart && val.reportTimeEnd) {
        this.dateRange = [
          val.reportTimeStart.substring(0, 10),
          val.reportTimeEnd.substring(0, 10)
        ]
      }

      if (val.createTimeStart && val.createTimeEnd) {
        const startYear = new Date(val.createTimeStart).getFullYear()
        this.selectedYear = startYear
      }
    }
  }

  created() {
    // 设置默认年度筛选（当前年份）
    const currentYear = new Date().getFullYear()
    this.selectedYear = currentYear
    this.filterParams.createTimeStart = `${currentYear}-01-01T00:00:00Z`
    this.filterParams.createTimeEnd = `${currentYear}-12-31T23:59:59Z`

    window.addEventListener('resize', this.handleResize)
  }

  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  }

  // 计算年度选项（近10年）
  private get yearOptions(): number[] {
    const currentYear = new Date().getFullYear()
    const years: number[] = []
    for (let i = 0; i < 10; i++) {
      years.push(currentYear - i)
    }
    return years
  }

  // 状态选项
  get statusOptions(): StatusOption[] {
    return [
      { value: LeakStatus.Unreported, label: GetLeakStatus(LeakStatus.Unreported) },
      { value: LeakStatus.NeedAssignRepairDept, label: GetLeakStatus(LeakStatus.NeedAssignRepairDept) },
      { value: LeakStatus.NeedAssignRepairUser, label: GetLeakStatus(LeakStatus.NeedAssignRepairUser) },
      { value: LeakStatus.Repairing, label: GetLeakStatus(LeakStatus.Repairing) },
      { value: LeakStatus.Repaired, label: GetLeakStatus(LeakStatus.Repaired) },
      { value: LeakStatus.NoNeedRepair, label: GetLeakStatus(LeakStatus.NoNeedRepair) },
      { value: LeakStatus.CantRepair, label: GetLeakStatus(LeakStatus.CantRepair) }
    ]
  }

  // 维修类型选项
  get repairTypeOptions(): RepairTypeOption[] {
    return [
      { value: RepairType.Other, label: GetRepairType(RepairType.Other) },
      { value: RepairType.BeforeMeter, label: GetRepairType(RepairType.BeforeMeter) },
      { value: RepairType.AfterMeter, label: GetRepairType(RepairType.AfterMeter) },
      { value: RepairType.OutSideNets, label: GetRepairType(RepairType.OutSideNets) },
      { value: RepairType.ChangePipe, label: GetRepairType(RepairType.ChangePipe) },
      { value: RepairType.Empty, label: GetRepairType(RepairType.Empty) },
      { value: RepairType.Internal, label: GetRepairType(RepairType.Internal) }
    ]
  }


  // 获取移动模式状态
  get isMobile() {
    return IsMobile()
  }

  // 检测窗口大小变化
  private handleResize() {
  }

  // 搜索处理
  private handleSearch() {
    // 发送过滤参数给父组件
    this.$emit('filter-change', { ...this.filterParams })
  }

  // 移动端搜索确认按钮处理
  private handleMobileSearch() {
    this.showSearchDialog = false
    this.handleSearch()
  }

  // 重置过滤器
  private resetFilter() {
    const currentYear = new Date().getFullYear()
    this.filterParams = {
      status: [LeakStatus.NeedAssignRepairDept, LeakStatus.NeedAssignRepairUser, LeakStatus.Repairing, LeakStatus.Repaired, LeakStatus.NoNeedRepair, LeakStatus.CantRepair], // 重置时选择除了未上报的所有状态
      keywords: [''] as string[],
      addressKeywords: [''] as string[],
      reportTimeStart: '', // 默认不设置上报时间筛选
      reportTimeEnd: '',
      createTimeStart: `${currentYear}-01-01T00:00:00Z`,
      createTimeEnd: `${currentYear}-12-31T23:59:59Z`,
      repairTypes: [],
      page: 1,
      pageSize: 10
    }
    this.dateRange = null
    this.selectedYear = currentYear

    // 通知父组件重置事件
    this.$emit('filter-reset')

    if (this.isMobile && this.showSearchDialog) {
      // 移动端重置不关闭弹窗
      this.$message({
        message: '搜索条件已重置',
        type: 'success'
      })
    } else {
      // 触发搜索以应用重置的过滤条件
      this.handleSearch()
    }
  }

  // 处理年度变化
  private handleYearChange(year: number | null) {
    if (year) {
      // 设置创建时间的年度范围
      this.filterParams.createTimeStart = `${year}-01-01T00:00:00Z`
      this.filterParams.createTimeEnd = `${year}-12-31T23:59:59Z`

      // 如果有上报时间筛选，则限制上报时间在所选年度内
      if (this.dateRange) {
        const startYear = new Date(this.dateRange[0]).getFullYear()
        const endYear = new Date(this.dateRange[1]).getFullYear()

        // 如果日期范围不在选择的年度内，重置为年度范围
        if (startYear != year || endYear != year) {
          this.dateRange = [`${year}-01-01`, `${year}-12-31`]
          this.filterParams.reportTimeStart = `${year}-01-01T00:00:00Z`
          this.filterParams.reportTimeEnd = `${year}-12-31T23:59:59Z`
          this.$message({
            message: `已将上报时间范围调整为${year}年`,
            type: 'info'
          })
        }
      } else {
        // 如果没有选择上报时间，则清空上报时间筛选条件
        this.filterParams.reportTimeStart = ''
        this.filterParams.reportTimeEnd = ''
      }
    } else {
      // 清空年度筛选
      this.filterParams.createTimeStart = ''
      this.filterParams.createTimeEnd = ''
      this.filterParams.reportTimeStart = ''
      this.filterParams.reportTimeEnd = ''
      this.dateRange = null
    }
  }

  // 处理日期范围变化
  private handleDateRangeChange(range: [string, string] | null) {
    if (range && range.length === 2) {
      const startDate = new Date(range[0])
      const endDate = new Date(range[1])
      const startYear = startDate.getFullYear()
      const endYear = endDate.getFullYear()

      // 如果已选择年度，检查日期范围是否在所选年度内
      if (this.selectedYear) {
        // 如果日期范围不在当前选择的年度内
        if (startYear != this.selectedYear || endYear != this.selectedYear) {
          // 调整日期范围到所选年度内
          const year = this.selectedYear
          this.dateRange = [`${year}-01-01`, `${year}-12-31`]
          this.filterParams.reportTimeStart = `${year}-01-01T00:00:00Z`
          this.filterParams.reportTimeEnd = `${year}-12-31T23:59:59Z`
          this.$message({
            message: `已将上报时间范围调整为${year}年`,
            type: 'info'
          })
          return
        }
      } else {
        // 如果跨年，则自动选择对应的年份
        if (startYear !== endYear) {
          // 使用开始日期的年份作为筛选年份
          this.selectedYear = startYear
          this.filterParams.createTimeStart = `${startYear}-01-01T00:00:00Z`
          this.filterParams.createTimeEnd = `${startYear}-12-31T23:59:59Z`
          // 限制结束日期在该年内
          this.dateRange = [range[0], `${startYear}-12-31`]
          this.filterParams.reportTimeStart = `${range[0]}T00:00:00Z`
          this.filterParams.reportTimeEnd = `${startYear}-12-31T23:59:59Z`
          this.$message({
            message: `检测到跨年选择，已将年度设置为${startYear}年，并调整上报时间范围`,
            type: 'info'
          })
          return
        } else {
          // 如果在同一年内，自动设置年度筛选
          this.selectedYear = startYear
          this.filterParams.createTimeStart = `${startYear}-01-01T00:00:00Z`
          this.filterParams.createTimeEnd = `${startYear}-12-31T23:59:59Z`
          this.filterParams.reportTimeStart = `${range[0]}T00:00:00Z`
          this.filterParams.reportTimeEnd = `${range[1]}T23:59:59Z`
        }
      }
    } else {
      // 清空日期范围
      this.filterParams.reportTimeStart = ''
      this.filterParams.reportTimeEnd = ''
    }
  }
}
